// Effets parallax au scroll, légers et throttlés via requestAnimationFrame.
// 1. .c-bg-decor : logo décoratif de fond, translaté selon la PROGRESSION de page.
// 2. [data-parallax] : images (c-feature, hero) décalées selon leur position relative
//    au centre du viewport — seuls les éléments visibles sont calculés (IntersectionObserver).
//    Le JS ne pose qu'une variable CSS --parallax-y ; le CSS décide comment l'utiliser
//    (translate de l'image, du calque hero…), ce qui évite d'écraser un éventuel scale.
// Tout est neutralisé sous prefers-reduced-motion (rendu statique).
export function initParallax() {
  const decor = document.querySelector(".c-bg-decor");
  const items = Array.from(document.querySelectorAll("[data-parallax]"));
  if (!decor && items.length === 0) return;

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  const visible = new Set();
  let ticking = false;

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  // Ne calcule que les éléments à l'écran (perf).
  const io =
    items.length > 0 && "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            for (const e of entries) {
              if (e.isIntersecting) visible.add(e.target);
              else visible.delete(e.target);
            }
            requestUpdate();
          },
          { rootMargin: "10% 0px" }
        )
      : null;

  // Sans IntersectionObserver : on retombe sur l'ensemble des éléments.
  const targets = io ? visible : new Set(items);

  const update = () => {
    ticking = false;

    // Phase LECTURE (layout) — tous les accès layout d'abord, pour éviter le thrashing
    // (lire un rect après avoir écrit un style force un recalcul synchrone).
    const mid = window.innerHeight / 2;
    const scrollY = window.scrollY;
    const scrollable =
      document.documentElement.scrollHeight - window.innerHeight;
    const shifts = [];
    for (const el of targets) {
      const speed = parseFloat(el.dataset.parallaxSpeed) || 0.08;
      const rect = el.getBoundingClientRect();
      const elCenter = rect.top + rect.height / 2;
      // (elCenter - mid) : l'élément commence DÉCALÉ VERS LE BAS quand il est bas dans le
      // viewport (à l'entrée), puis remonte en sortant par le haut — sens de la réf.
      shifts.push({ el, y: (elCenter - mid) * speed });
    }

    // Phase ÉCRITURE (style) — aucune lecture layout ici.
    if (decor) {
      // Décor de fond : offset basé sur la progression du scroll (0→1).
      const progress = scrollable > 0 ? scrollY / scrollable : 0;
      const offset = (progress - 0.5) * (window.innerHeight / 2);
      decor.style.transform = `translate3d(0, ${offset}px, 0)`;
    }
    for (const { el, y } of shifts) {
      el.style.setProperty("--parallax-y", `${y.toFixed(1)}px`);
    }
  };

  const clear = () => {
    if (decor) decor.style.transform = "";
    for (const el of items) el.style.removeProperty("--parallax-y");
  };

  const apply = () => {
    window.removeEventListener("scroll", requestUpdate);
    window.removeEventListener("resize", requestUpdate);
    if (io) items.forEach((el) => io.unobserve(el));
    visible.clear();

    if (reduce.matches) {
      clear(); // rendu statique
      return;
    }
    if (io) items.forEach((el) => io.observe(el));
    update(); // position initiale (au cas où la page est déjà scrollée)
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });
  };

  apply();
  reduce.addEventListener("change", apply);
}
